import './Flex.less';

import cn from 'classnames';
import React, {
  CSSProperties,
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
} from 'react';
import { FC } from 'react';

import { Gutter } from '../../utils';

export type FlexProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  direction?: 'row' | 'col';
  row?: boolean;
  col?: boolean;
  reverse?: boolean;
  stretch?: boolean;
  inline?: boolean;
  width?: CSSProperties['width'];
  maxWidth?: CSSProperties['maxWidth'];
  minWidth?: CSSProperties['maxWidth'];
  position?: 'relative' | 'absolute' | 'static' | 'fixed';
  justify?:
    | 'flex-start'
    | 'stretch'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around';
  align?: 'flex-start' | 'stretch' | 'flex-end' | 'center';
  gap?: number;
};

// @ts-ignore because of Flex.Item
export const Flex: ForwardRefExoticComponent<
  PropsWithoutRef<FlexProps> & RefAttributes<HTMLDivElement>
> & { Item: FC<ItemsProps> } = forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      position,
      children,
      justify,
      align,
      direction,
      className,
      col,
      row,
      stretch,
      inline,
      width,
      gap,
      reverse,
      ...other
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn([
          className,
          position ? `ergo-flex-position--${position}` : '',
          'ergo-flex',
          `ergo-flex-direction--${
            (col && reverse && 'col-reverse') ||
            (row && reverse && 'row-reverse') ||
            (col && 'col') ||
            (row && 'row') ||
            direction
          }`,
          `ergo-flex-justify--${justify}`,
          `ergo-flex-align-items--${align}`,
          { 'ergo-flex-stretch': stretch },
          { 'ergo-flex-inline': inline },
        ])}
        style={{
          width,
          gap: gap && `calc(var(--spectrum-base-gutter) * ${gap})`,
          ...other?.style,
        }}
        {...other}
      >
        {children}
      </div>
    );
  },
);

Flex.displayName = 'Flex';

Flex.defaultProps = {
  direction: 'row',
  justify: 'stretch',
  align: 'stretch',
  gap: 0,
};

type ItemsProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  display?: 'flex' | 'block' | 'none' | 'inline-block';
  order?: number;
  alignSelf?: 'flex-start' | 'stretch' | 'flex-end' | 'center';
  marginBottom?: number | 'auto';
  marginTop?: number | 'auto';
  marginLeft?: number | 'auto';
  marginRight?: number | 'auto';
  margin?: Gutter;
  flex?: number;
  grow?: boolean;
} & FlexProps;

const Item: FC<ItemsProps> = ({
  alignSelf,
  children,
  display,
  style,
  order,
  marginBottom,
  marginTop,
  marginLeft,
  marginRight,
  flex,
  grow,
  justify,
  direction,
  align,
  col,
  row,
  className,
  maxWidth,
  minWidth,
  width,
  ...other
}) => (
  <div
    className={cn([
      className,
      'ergo-flex',
      `ergo-flex-direction--${(col && 'col') || (row && 'row') || direction}`,
      `ergo-flex-justify--${justify}`,
      `ergo-flex-align-self--${alignSelf}`,
      `ergo-flex-align-items--${align}`,
      { 'ergo-flex-item__grow': grow },
    ])}
    style={{
      order,
      display: display || (justify || direction || align ? 'flex' : 'initial'),
      flex,
      marginBottom:
        marginBottom === 'auto'
          ? 'auto'
          : `calc(var(--spectrum-base-gutter) * ${marginBottom})`,
      marginTop:
        marginTop === 'auto'
          ? 'auto'
          : `calc(var(--spectrum-base-gutter) * ${marginTop})`,
      marginRight:
        marginRight === 'auto'
          ? 'auto'
          : `calc(var(--spectrum-base-gutter) * ${marginRight})`,
      marginLeft:
        marginLeft === 'auto'
          ? 'auto'
          : `calc(var(--spectrum-base-gutter) * ${marginLeft})`,
      width,
      maxWidth,
      minWidth,
      ...style,
    }}
    {...other}
  >
    {children}
  </div>
);
Item.defaultProps = {
  order: undefined,
  flex: undefined,
  row: true,
  marginBottom: 0,
  marginRight: 0,
  marginLeft: 0,
  marginTop: 0,
};

Flex.Item = Item;
